import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { Box, IconButton, Chip } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useCreateTicketMutation, useUploadFileToTicketMutation } from 'services/api/tickets.api';
import type { CreateTicketRequest } from 'services/types/ticket';

import BusinessUnitSelector from './BusinessUnitsSelector';

interface CreateTicketDialogProps {
  open: boolean;
  onClose: () => void;
  clientId?: number;
  businessUnitId?: number;
  isAdmin?: boolean;
  onSuccess?: (ticketId: number) => void;
}

const CreateTicketDialog: React.FC<CreateTicketDialogProps> = ({
  open,
  onClose,
  clientId,
  businessUnitId: initialBusinessUnitId,
  isAdmin = false,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [createTicket] = useCreateTicketMutation();
  const [uploadFile] = useUploadFileToTicketMutation();

  // State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<number | undefined>(initialBusinessUnitId);
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // Validation
  const isTitleValid = title.trim().length >= 10;
  const isDescriptionValid = description.trim().length >= 20;
  const isValid = isTitleValid && isDescriptionValid;

  // Manejar pegado de archivos en el campo de descripción
  const handleDescriptionPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      setAttachedFiles((prev) => [...prev, ...files]);
      setError(null);
      event.preventDefault(); // Evita que se pegue como texto
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    setAttachedFiles((prev) => [...prev, ...files]);
    setError(null);
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFilesToTicket = async (ticketId: number) => {
    if (attachedFiles.length === 0) return;

    setIsUploadingFiles(true);
    try {
      for (let i = 0; i < attachedFiles.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await uploadFile({ ticketId, file: attachedFiles[i], showToUser: true });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error uploading file:', err);
      setError('Error al subir algunos archivos');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!isValid) return;

    setIsCreating(true);
    setError(null);

    try {
      const ticketData: CreateTicketRequest = {
        title: title.trim(),
        description: description.trim(),
        ...(clientId && { clientId }),
        ...(selectedBusinessUnitId && { businessUnitId: selectedBusinessUnitId }),
      };

      const result = await createTicket(ticketData).unwrap();

      console.log('result', result); // eslint-disable-line no-console
      setCreatedTicketId(result.id);

      // Upload files if any
      if (attachedFiles.length > 0) {
        await uploadFilesToTicket(result.id);
      }

      setShowSuccess(true);

      if (onSuccess) {
        onSuccess(result.id);
      }
    } catch (err) {
      setError('Error al crear el ticket. Por favor, intenta nuevamente.');
      // eslint-disable-next-line no-console
      console.error('Error creating ticket:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setTitle('');
      setDescription('');
      setSelectedBusinessUnitId(initialBusinessUnitId);
      setError(null);
      setShowSuccess(false);
      setCreatedTicketId(null);
      setAttachedFiles([]);
      onClose();
    }
  };

  const handleViewTicket = () => {
    if (createdTicketId) {
      navigate(`/tickets/${createdTicketId}`);
    }
  };

  const getTitleHelperText = () => {
    if (!title) return '';
    if (!isTitleValid) return `Mínimo 10 caracteres (${title.trim().length}/10)`;
    return `${title.trim().length} caracteres`;
  };

  const getDescriptionHelperText = () => {
    if (!description) return '';
    if (!isDescriptionValid) return `Mínimo 20 caracteres (${description.trim().length}/20)`;
    return `${description.trim().length} caracteres`;
  };

  if (showSuccess) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Ticket Creado</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            El ticket ha sido creado exitosamente.
            {attachedFiles.length > 0 && ' Los archivos han sido subidos.'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            ¿Qué te gustaría hacer ahora?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
          <Button onClick={handleViewTicket} variant="contained">
            Ver Ticket
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nuevo Ticket</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            label="Título/Problema"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!title && !isTitleValid}
            helperText={getTitleHelperText()}
            disabled={isCreating}
          />

          <TextField
            label="Descripción"
            placeholder="Incluye todo lo necesario para entender el problema y que nuestro equipo pueda ayudarte lo más rápido posible"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!description && !isDescriptionValid}
            helperText={getDescriptionHelperText()}
            disabled={isCreating}
            onPaste={handleDescriptionPaste}
          />

          {/* File Upload Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Archivos adjuntos ({attachedFiles.length})
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <IconButton component="label" disabled={isCreating} sx={{ border: '1px dashed #ccc', borderRadius: 1 }}>
                <AttachFileIcon />
                <input
                  type="file"
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xls,.xlsx,video/*"
                  onChange={handleFileSelect}
                  multiple
                  disabled={isCreating}
                />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                Arrastra archivos aquí o haz clic para seleccionar
              </Typography>
            </Box>

            {/* File List */}
            {attachedFiles.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {attachedFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    label={file.name}
                    onDelete={() => removeFile(index)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>

          {isAdmin && !initialBusinessUnitId && (
            <BusinessUnitSelector
              businessUnitId={selectedBusinessUnitId || null}
              setBusinessUnitId={(id: number) => setSelectedBusinessUnitId(id)}
              selectItemIfOnlyOne
            />
          )}

          {!clientId && (
            <Typography variant="body2" color="text.secondary">
              Si es sobre un cliente en específico, entonces incluye el id del cliente en la descripción o crea el
              ticket desde la vista de ese cliente en específico
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancelar
        </Button>
        <Button
          onClick={() => {
            handleCreateTicket().catch((err) => {
              // eslint-disable-next-line no-console
              console.error('Error creating ticket:', err);
            });
          }}
          variant="contained"
          disabled={!isValid || isCreating || isUploadingFiles}
        >
          {isCreating || isUploadingFiles ? 'Creando...' : 'Crear Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateTicketDialog.defaultProps = {
  clientId: undefined,
  businessUnitId: undefined,
  isAdmin: false,
  onSuccess: undefined,
};

export default CreateTicketDialog;
